import { useEffect, useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { getDeviceId, getDeviceName } from '../utils/deviceId';
import { preferencesDriveService } from '../lib/preferences-drive';

type Theme = 'light' | 'dark';

export const useTheme = () => {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first for immediate UI
    const savedTheme = localStorage.getItem('nidonote-theme') as Theme | null;
    if (savedTheme) return savedTheme;

    // Check system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load theme from Google Drive on mount (if authenticated)
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    // Note: Drive client is initialized by useDrive hook
    loadThemeFromGoogleDrive();
  }, [user]);

  const loadThemeFromGoogleDrive = async () => {
    if (!user) return;

    try {
      const deviceId = getDeviceId();

      console.log('📥 Loading web theme from Google Drive for device:', deviceId);

      // Get web theme for this device
      const webTheme = await preferencesDriveService.getWebTheme(deviceId);

      if (webTheme) {
        console.log('✅ Web theme loaded from Google Drive:', webTheme);
        setThemeState(webTheme);
        localStorage.setItem('nidonote-theme', webTheme);
      } else {
        console.log('ℹ️ No saved web theme found in Google Drive for this device');
      }
    } catch (error) {
      console.error('❌ Error loading theme from Google Drive:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveThemeToGoogleDrive = useCallback(
    async (newTheme: Theme) => {
      if (!user) {
        console.log('⚠️ Web theme not saved: User not authenticated');
        return;
      }

      try {
        const deviceId = getDeviceId();
        const deviceName = getDeviceName();

        console.log('💾 Saving web theme to Google Drive:', {
          webTheme: newTheme,
          deviceId,
          deviceName,
          userId: user.uid,
        });

        await preferencesDriveService.updateWebTheme(deviceId, deviceName, newTheme);

        console.log('✅ Web theme saved successfully to Google Drive');
      } catch (error) {
        console.error('❌ Error saving web theme to Google Drive:', error);
      }
    },
    [user]
  );

  // Apply theme to DOM
  useEffect(() => {
    const root = window.document.documentElement;
    const appRoot = window.document.getElementById('root');

    if (theme === 'dark') {
      root.classList.add('dark');
      if (appRoot) appRoot.classList.add('dark');
    } else {
      root.classList.remove('dark');
      if (appRoot) appRoot.classList.remove('dark');
    }

    // Save to localStorage for quick loading
    localStorage.setItem('nidonote-theme', theme);

    // Save to Google Drive if user is authenticated
    if (user && !isLoading) {
      saveThemeToGoogleDrive(theme);
    }
  }, [theme, user, isLoading, saveThemeToGoogleDrive]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === 'dark',
    isLoading,
  };
};
